import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateFAQPageSchema } from '../../utils/seo';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Is this a medical diagnosis tool?',
      answer: 'No, CerviShield is NOT a diagnostic tool. It provides educational risk assessment and guidance only. Our AI-powered platform helps you understand potential risk factors based on your inputs, but it does not diagnose cervical cancer or any medical condition. Always consult qualified healthcare professionals for medical diagnosis and treatment.',
      category: 'General',
    },
    {
      question: 'Is my personal data stored or shared?',
      answer: 'No, we do not store any of your personal data. All information you provide is processed anonymously and temporarily for the assessment only. Once you close your session, no data is retained on our servers. We prioritize your privacy and security at every step.',
      category: 'Privacy',
    },
    {
      question: 'Should I see a doctor even if my risk is low?',
      answer: 'Yes, absolutely. Even with a low-risk assessment, regular screening is essential for cervical health. Our platform provides guidance, but it cannot replace professional medical examination. We recommend following standard screening guidelines: Pap tests every 3 years for ages 21-29, and Pap + HPV tests every 5 years for ages 30-65.',
      category: 'Medical',
    },
    {
      question: 'Who should use this platform?',
      answer: 'CerviShield is designed for women seeking to understand cervical health, assess their risk factors, and learn about prevention. It\'s particularly useful for those with limited access to healthcare information, individuals unsure about symptoms, or anyone wanting to be proactive about their health. However, it\'s educational only and should not replace medical consultation.',
      category: 'General',
    },
    {
      question: 'How accurate is the AI risk assessment?',
      answer: 'Our AI model is based on established medical research and risk factors identified in clinical studies. However, it has limitations and cannot account for all individual circumstances. The assessment provides general guidance only and should not be considered definitive. Medical screening by healthcare professionals is the only accurate way to detect cervical cancer or precancerous changes.',
      category: 'Technology',
    },
    {
      question: 'What if I have symptoms?',
      answer: 'If you are experiencing any symptoms such as abnormal bleeding, pelvic pain, unusual discharge, or other concerning signs, please consult a healthcare provider immediately. Do not rely solely on our assessment. Symptoms require professional medical evaluation, and early consultation can make a significant difference in outcomes.',
      category: 'Medical',
    },
    {
      question: 'Is this service free?',
      answer: 'Yes, CerviShield is completely free to use. Our mission is to make cervical cancer awareness and risk guidance accessible to everyone, regardless of economic status. We do not charge for assessments, educational resources, or hospital directory access.',
      category: 'General',
    },
    {
      question: 'Can men use this platform?',
      answer: 'While cervical cancer specifically affects women, men can use this platform to learn about cervical cancer, understand risk factors, and access educational resources. This knowledge can help men support the women in their lives and promote awareness in their communities.',
      category: 'General',
    },
    {
      question: 'What age should I start screening?',
      answer: 'According to medical guidelines, cervical cancer screening should begin at age 21, regardless of sexual activity. Ages 21-29 should have Pap tests every 3 years. Ages 30-65 should have Pap + HPV tests every 5 years (or Pap test alone every 3 years). Women over 65 with adequate prior screening may stop. Always discuss with your healthcare provider.',
      category: 'Medical',
    },
    {
      question: 'Is HPV the same as cervical cancer?',
      answer: 'No, HPV (Human Papillomavirus) is not the same as cervical cancer. HPV is a common virus, and most infections clear on their own. However, persistent infection with high-risk HPV types can lead to cervical cancer over many years (typically 10-20 years). This is why regular screening is important—it can detect precancerous changes before cancer develops.',
      category: 'Medical',
    },
    {
      question: 'Can I prevent cervical cancer?',
      answer: 'Yes! Cervical cancer is one of the most preventable cancers. Prevention strategies include: HPV vaccination (ideally before sexual activity begins), regular screening (Pap and HPV tests), not smoking, practicing safe sex, and maintaining a healthy lifestyle. Early detection through screening can identify precancerous changes that can be treated before cancer develops.',
      category: 'Prevention',
    },
    {
      question: 'What is the HPV vaccine?',
      answer: 'The HPV vaccine protects against the types of HPV that cause most cervical cancers. It\'s most effective when given before exposure to HPV (ideally ages 11-12), but catch-up vaccination is available up to age 26. The vaccine is safe, effective, and can prevent up to 90% of HPV-related cancers. Discuss with your healthcare provider about vaccination.',
      category: 'Prevention',
    },
    {
      question: 'Are the hospitals in your directory verified?',
      answer: 'The hospitals listed in our directory are well-known cancer care facilities in India. However, we do not have partnerships with these hospitals and cannot verify real-time information about services, availability, or costs. We recommend contacting hospitals directly to confirm details. We prioritize listing government hospitals that offer affordable or free services.',
      category: 'Resources',
    },
    {
      question: 'What if I can\'t afford screening or treatment?',
      answer: 'Many government hospitals and cancer institutes in India provide free or heavily subsidized screening and treatment services. Our hospital directory prioritizes these facilities. Additionally, various government schemes and NGOs offer support for cancer care. Contact the National Cancer Helpline (1800-11-4477) for guidance on accessing affordable care.',
      category: 'Resources',
    },
    {
      question: 'Can I use this if I\'ve already been diagnosed?',
      answer: 'If you\'ve already been diagnosed with cervical cancer or precancerous conditions, this platform can provide educational information, but you should follow your healthcare provider\'s treatment plan. Our assessment tool is designed for risk guidance, not for managing existing diagnoses. Always prioritize your doctor\'s advice.',
      category: 'Medical',
    },
    {
      question: 'How often should I use this assessment?',
      answer: 'You can use our assessment tool periodically to understand your risk factors, but it should not replace regular medical screening. We recommend using it as an educational tool to stay informed about cervical health, but always follow standard screening guidelines and your healthcare provider\'s recommendations.',
      category: 'General',
    },
    {
      question: 'What makes CerviShield different from other health websites?',
      answer: 'CerviShield combines AI-powered risk assessment with comprehensive educational resources specifically focused on cervical cancer. We prioritize privacy (no data storage), accessibility (free for all), and social impact (highlighting affordable care options). Our mission is public health education and early awareness, not commercial gain.',
      category: 'General',
    },
    {
      question: 'Can I share my results with my doctor?',
      answer: 'While we don\'t provide downloadable reports, you can discuss the risk factors and guidance you received with your healthcare provider. However, remember that our assessment is educational only. Your doctor will conduct proper medical screening and diagnosis based on clinical examination and tests.',
      category: 'Medical',
    },
  ];

  const categories = ['All', ...Array.from(new Set(faqs.map(faq => faq.category)))];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredFaqs = selectedCategory === 'All' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  // SEO Configuration with FAQPage schema
  useSEO({
    title: 'Frequently Asked Questions | CerviShield Cervical Cancer Awareness',
    description: 'Find answers to common questions about CerviShield, cervical cancer screening, HPV testing, risk assessment, and prevention. Learn about our AI tool, privacy policy, and when to seek medical care.',
    keywords: 'cervical cancer FAQ, HPV questions, screening questions, cancer prevention FAQ, health assessment questions',
    canonical: '/faq',
    schema: generateFAQPageSchema(
      faqs.map(faq => ({
        question: faq.question,
        answer: faq.answer,
      }))
    ),
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50/30">
      <Navbar />

      <div className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold font-display text-gray-800 mb-4">
              Frequently Asked <span className="text-primary">Questions</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about CerviShield, cervical cancer, and our services
            </p>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-primary to-teal-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors duration-300"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full flex-shrink-0 mt-1">
                      <i className="ri-question-line text-primary text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-1">{faq.question}</h3>
                      <span className="text-xs text-primary font-semibold">{faq.category}</span>
                    </div>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openIndex === index && (
                  <div className="px-8 pb-6 animate-fade-in">
                    <div className="pl-14 pr-4">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-12 text-center">
            <i className="ri-question-answer-line text-5xl text-primary mb-6"></i>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Still Have Questions?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              If you couldn't find the answer you're looking for, please reach out to healthcare professionals or contact the National Cancer Helpline for guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:1800114477"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-teal-500 text-white rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Call Helpline
              </a>
              <a
                href="/emergency"
                className="inline-flex items-center gap-3 px-8 py-4 border-2 border-primary text-primary rounded-full text-lg font-semibold hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Emergency Guidance
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
